import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAds, trackAdClick, trackAdImpression } from '../useAds';
import { getAdUrl } from '../ads.types';
import { AD_SLOTS } from '../mockAds';

interface AdHeroCarouselProps {
  slotName?: string;
  autoPlayInterval?: number;
}

function AdHeroCarouselSkeleton() {
  return (
    <div className="relative w-full aspect-[1.5/1] md:aspect-[3/1] overflow-hidden bg-gray-200 animate-pulse border border-gray-100">
      <div className="absolute inset-0 flex items-center">
        <div className="w-full max-w-7xl mx-auto px-16 md:px-20 lg:px-24">
          <div className="max-w-xl space-y-4">
            <div className="h-12 w-3/4 bg-gray-300 rounded" />
            <div className="h-6 w-full bg-gray-300 rounded" />
            <div className="h-6 w-2/3 bg-gray-300 rounded" />
            <div className="h-12 w-32 bg-gray-300 rounded-lg" />
          </div>
        </div>
      </div>
    </div>
  );
}

function AdHeroCarousel({ slotName = AD_SLOTS.HP_HERO, autoPlayInterval = 5000 }: AdHeroCarouselProps) {
  const { data, isLoading, error } = useAds(slotName);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const navigate = useNavigate();

  const slides = data?.items ?? [];

  // Touch handling for swipe
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsAutoPlaying(false);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      goToNext();
    }
    if (isRightSwipe) {
      goToPrev();
    }

    // Reset
    setTouchStart(0);
    setTouchEnd(0);
  };

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlaying || slides.length <= 1) return;

    const timer = setInterval(goToNext, autoPlayInterval);
    return () => clearInterval(timer);
  }, [isAutoPlaying, autoPlayInterval, goToNext, slides.length]);

  // Resume auto-play after user interaction
  useEffect(() => {
    if (isAutoPlaying) return;

    const resumeTimer = setTimeout(() => {
      setIsAutoPlaying(true);
    }, 10000);

    return () => clearTimeout(resumeTimer);
  }, [isAutoPlaying, currentIndex]);

  // Track impressions when slide changes
  useEffect(() => {
    if (slides.length > 0 && data) {
      const currentSlide = slides[currentIndex];
      trackAdImpression(data.slot_id, currentSlide.id);
    }
  }, [currentIndex, slides, data]);

  const handleSlideClick = (slideIndex: number) => {
    if (!data) return;

    const slide = slides[slideIndex];
    const url = getAdUrl(slide, data.slot_id);

    trackAdClick(data.slot_id, slide.id, url);
    navigate(url);
  };

  const handleNavClick = (e: React.MouseEvent, direction: 'prev' | 'next') => {
    e.stopPropagation();
    setIsAutoPlaying(false);
    if (direction === 'prev') {
      goToPrev();
    } else {
      goToNext();
    }
  };

  if (isLoading) {
    return <AdHeroCarouselSkeleton />;
  }

  if (error || !data || slides.length === 0) {
    return null; 
  }

  return (
    <div
      className="relative w-full aspect-[1.5/1] md:aspect-[3/1] h-500 overflow-hidden group"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slides */}
      <div
        className="flex h-full transition-transform duration-500 ease-out"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className="relative w-full h-full shrink-0 cursor-pointer"
            onClick={() => handleSlideClick(index)}
          >
            <picture className="absolute inset-0 w-full h-full">
              <source media="(min-width: 768px)" srcSet={slide.desktop_image || slide.image} />
              <img
                src={slide.mobile_image || slide.image}
                alt={slide.headline || 'Advertisement'}
                className="w-full h-full object-cover"
              />
            </picture>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={(e) => handleNavClick(e, 'prev')}
            className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/80 border border-gray-100 flex items-center justify-center text-gray-700 hover:bg-white hover:scale-105 transition-all"
            aria-label="Previous slide"
          >
            <ChevronLeft className="size-5 md:size-6" />
          </button>
          <button
            onClick={(e) => handleNavClick(e, 'next')}
            className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/80 border border-gray-100 flex items-center justify-center text-gray-700 hover:bg-white hover:scale-105 transition-all"
            aria-label="Next slide"
          >
            <ChevronRight className="size-5 md:size-6" />
          </button>
        </>
      )}

      {/* Dot Indicators */}
      {slides.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${index === currentIndex ? 'bg-white w-8' : 'bg-white/50 hover:bg-white/70'
                }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default AdHeroCarousel;
